import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// Alarm Form Component - UPDATED VERSION (sin campo eje)
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Alert,
  Chip,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useApiCall, useMutation } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useNotification } from '../common/GlobalNotifications';

// UPDATED: Validation schema - SIN axle
const validationSchema = Yup.object({
  vehicle_id: Yup.string().required(i18n.t('hardcoded.components_alarms_alarmform.el_vehiculo_es_requerido')),
  metric_type: Yup.string().required(i18n.t('hardcoded.components_alarms_alarmform.el_tipo_de_metrica_es')),
  threshold_min: Yup.number()
    .nullable()
    .min(0, i18n.t('hardcoded.components_alarms_alarmform.el_umbral_minimo_debe_ser')),
  threshold_max: Yup.number()
    .nullable()
    .min(0, i18n.t('hardcoded.components_alarms_alarmform.el_umbral_maximo_debe_ser'))
    .when('threshold_min', (threshold_min, schema) => {
      return threshold_min != null
        ? schema.min(threshold_min, 'El umbral máximo debe ser mayor que el mínimo')
        : schema;
    }),
});

const AlarmForm = ({
  open,
  onClose,
  alarm = null,
  onSuccess,
  vehicleId = null
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // API calls
  const { data: vehicles = [], loading: vehiclesLoading } = useApiCall(api.vehicles.getAll);

  const { mutate: createAlarm, loading: creating, error: createError } = useMutation(
    api.alarms.create,
    {
      onSuccess: (data) => {
        console.log('✅ Alarm creation success:', data);
        // Verificar si la respuesta tiene la estructura esperada
        if (data && data.data) {
          if (data.data.total_created && data.data.total_created > 1) {
            showSuccess(t('hardcoded.components_alarms_alarmform.total_created_alarmas_creadas_exitosamente_para', { total_created: data.data.total_created }));
          } else {
            showSuccess(t('hardcoded.components_alarms_alarmform.alarmas_creadas_exitosamente'));
          }
          onSuccess?.(data.data);
        } else {
          // Si no tiene la estructura esperada pero llegó aquí, es que funcionó
          showSuccess(t('hardcoded.components_alarms_alarmform.alarmas_creadas_exitosamente'));
          onSuccess?.(data);
        }
        onClose();
        formik.resetForm();
      },
      onError: (error) => {
        console.error('❌ Alarm creation error:', error);
        console.error(t('hardcoded.components_alarms_alarmform.error_response'), error.response?.data);
        console.error('Error status:', error.response?.status);

        // Verificar si es un error real o si las alarmas se crearon
        if (error.response?.status >= 500) {
          // Error del servidor, pero tal vez se crearon las alarmas
          showError(t('hardcoded.components_alarms_alarmform.error_del_servidor_pero_las'));
          // Refrescar para ver si se crearon
          setTimeout(() => {
            onSuccess?.();
          }, 1000);
        } else {
          showError(error.response?.data?.detail || error.message || t('hardcoded.components_alarms_alarmform.error_al_crear_la_alarma'));
        }
      }
    }
  );

  const { mutate: updateAlarm, loading: updating, error: updateError } = useMutation(
    (data) => api.alarms.update(alarm.id, data),
    {
      onSuccess: (data) => {
        console.log('Alarm update success:', data);
        showSuccess(t('hardcoded.components_alarms_alarmform.alarma_actualizada_exitosamente'));
        onSuccess?.(data);
        onClose();
        formik.resetForm();
      },
      onError: (error) => {
        console.error('Alarm update error:', error);
        showError(error.message || t('hardcoded.components_alarms_alarmform.error_al_actualizar_la_alarma'));
      }
    }
  );

  const formik = useFormik({
    initialValues: {
      vehicle_id: alarm?.vehicle_id || vehicleId || '',
      metric_type: alarm?.metric_type || '',
      threshold_min: alarm?.threshold_min || '',
      threshold_max: alarm?.threshold_max || '',
      active: alarm?.active ?? true,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        console.log('Form submission started with values:', values);

        // Limpiar y preparar los datos - SIN axle
        const cleanData = {
          vehicle_id: values.vehicle_id,
          metric_type: values.metric_type,
          active: Boolean(values.active),
        };

        // Manejar threshold_min
        if (values.threshold_min !== '' && values.threshold_min !== null && values.threshold_min !== undefined) {
          const minVal = parseFloat(values.threshold_min);
          if (!isNaN(minVal)) {
            cleanData.threshold_min = minVal;
          }
        } else {
          cleanData.threshold_min = null;
        }

        // Manejar threshold_max
        if (values.threshold_max !== '' && values.threshold_max !== null && values.threshold_max !== undefined) {
          const maxVal = parseFloat(values.threshold_max);
          if (!isNaN(maxVal)) {
            cleanData.threshold_max = maxVal;
          }
        } else {
          cleanData.threshold_max = null;
        }

        // Validación adicional
        if (cleanData.threshold_min !== null && cleanData.threshold_max !== null) {
          if (cleanData.threshold_min >= cleanData.threshold_max) {
            showError(t('hardcoded.components_alarms_alarmform.el_umbral_minimo_debe_ser'));
            return;
          }
        }

        console.log('Sending alarm data:', cleanData);

        if (alarm) {
          console.log('Updating existing alarm:', alarm.id);
          await updateAlarm(cleanData);
        } else {
          console.log('Creating new alarm(s)');
          await createAlarm(cleanData);
        }
      } catch (error) {
        console.error('Error in form submission:', error);
        showError(t('hardcoded.components_alarms_alarmform.error_al_procesar_el_formulario'));
      }
    },
  });

  // Update selected vehicle when vehicle_id changes
  useEffect(() => {
    if (formik.values.vehicle_id && vehicles.length > 0) {
      const vehicle = vehicles.find(v => v.id === formik.values.vehicle_id);
      setSelectedVehicle(vehicle);
    } else {
      setSelectedVehicle(null);
    }
  }, [formik.values.vehicle_id, vehicles]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setSelectedVehicle(null);
    }
  }, [open]);

  const handleClose = () => {
    formik.resetForm();
    setSelectedVehicle(null);
    onClose();
  };

  const loading = creating || updating;
  const isEditing = Boolean(alarm);
  const error = createError || updateError;

  // Sensor types hardcoded since SENSOR_TYPES was removed
  const sensorTypes = [
    { id: 'PSI', name: t('hardcoded.components_alarmprofiles_profilecard.presion') },
    { id: 'TEMP', name: t('hardcoded.components_alarmprofiles_profilecard.temperatura') },
    { id: 'ACC', name: t('hardcoded.components_alarmprofiles_profileform.acelerometro') },
    { id: 'GPS', name: 'GPS' }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {isEditing ? t('hardcoded.components_alarms_alarmform.editar_alarma') : t('hardcoded.components_alarms_alarmform.crear_nueva_alarma')}
          </Typography>
          <Chip
            label={formik.values.active ? t('hardcoded.components_ai_aimemoryeditor.activa') : t('hardcoded.components_ai_aimemoryeditor.inactiva')}
            color={formik.values.active ? 'success' : 'default'}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error.message || t('hardcoded.components_alarms_alarmform.ocurrio_un_error_inesperado')}
          </Alert>
        )}

        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Vehicle Selection */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={formik.touched.vehicle_id && Boolean(formik.errors.vehicle_id)}
                disabled={vehiclesLoading || loading || isEditing}
              >
                <InputLabel>{t('vehicles.table.vehicle')}</InputLabel>
                <Select
                  name="vehicle_id"
                  value={formik.values.vehicle_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label={t('vehicles.table.vehicle')}
                >
                  {(vehicles || []).map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      <Box>
                        <Typography variant="body1">
                          {vehicle.display_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {vehicle.configuration_p} • {vehicle.axle_count} {i18n.t('hardcoded.components_alarmprofiles_profiledetail.ejes')}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.vehicle_id && formik.errors.vehicle_id && (
                  <FormHelperText>
                    {formik.errors.vehicle_id}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* REMOVED: Axle field - ya no se necesita */}

            {/* Vehicle Info Display */}
            {selectedVehicle && !isEditing && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {t('hardcoded.components_alarms_alarmform.se_crearan_alarmas_para_todos')}</Typography>
                  <List dense sx={{ mt: 1 }}>
                    {Array.from({ length: selectedVehicle.axle_count }, (_, i) => i + 1).map(axleNum => (
                      <ListItem key={axleNum} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={t('hardcoded.components_alarmprofiles_profileform.eje_axlenum', { axleNum: axleNum })}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {i18n.t('hardcoded.components_admin_ordermanagement.total')}{selectedVehicle.axle_count} {t('hardcoded.components_alarms_alarmform.alarmas_seran_creadas')}</Typography>
                </Alert>
              </Grid>
            )}

            {/* Metric Type */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={formik.touched.metric_type && Boolean(formik.errors.metric_type)}
                disabled={loading || isEditing}
              >
                <InputLabel>{t('hardcoded.components_alarms_alarmform.tipo_de_metrica')}</InputLabel>
                <Select
                  name="metric_type"
                  value={formik.values.metric_type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label={t('hardcoded.components_alarms_alarmform.tipo_de_metrica')}
                >
                  {sensorTypes.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name} ({type.id})
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.metric_type && formik.errors.metric_type && (
                  <FormHelperText>
                    {formik.errors.metric_type}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Thresholds */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('hardcoded.components_alarms_alarmform.umbrales_aplicaran_a_todos_los')}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="threshold_min"
                label={t('hardcoded.components_alarms_alarmform.umbral_minimo')}
                type="number"
                value={formik.values.threshold_min}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.threshold_min && Boolean(formik.errors.threshold_min)}
                helperText={
                  formik.touched.threshold_min && formik.errors.threshold_min
                    ? formik.errors.threshold_min
                    : t('hardcoded.components_alarms_alarmform.dejar_vacio_para_no_tener')
                }
                inputProps={{
                  step: 0.1
                }}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="threshold_max"
                label={t('hardcoded.components_alarms_alarmform.umbral_maximo')}
                type="number"
                value={formik.values.threshold_max}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.threshold_max && Boolean(formik.errors.threshold_max)}
                helperText={
                  formik.touched.threshold_max && formik.errors.threshold_max
                    ? formik.errors.threshold_max
                    : t('hardcoded.components_alarms_alarmform.dejar_vacio_para_no_tener')
                }
                inputProps={{
                  step: 0.1
                }}
                disabled={loading}
              />
            </Grid>

            {/* Validation Warning */}
            {formik.values.threshold_min && formik.values.threshold_max &&
              parseFloat(formik.values.threshold_min) >= parseFloat(formik.values.threshold_max) && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    {t('hardcoded.components_alarms_alarmform.el_umbral_minimo_debe_ser')}</Alert>
                </Grid>
              )}

            {/* Active Switch */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="active"
                    checked={formik.values.active}
                    onChange={formik.handleChange}
                    color="primary"
                    disabled={loading}
                  />
                }
                label={t('navbar.activeAlarms')}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {isEditing
                  ? t('hardcoded.components_alarms_alarmform.las_alarmas_inactivas_no_activaran')
                  : t('hardcoded.components_alarms_alarmform.todas_las_alarmas_creadas_estaran')
                }
              </Typography>
            </Grid>

            {/* Edit mode warning */}
            {isEditing && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    {t('hardcoded.components_alarms_alarmform.editando_alarma_para')}<strong>{alarm?.vehicle_name} {i18n.t('hardcoded.components_alarms_alarmform.-_eje')}{alarm?.axle}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('hardcoded.components_alarms_alarmform.los_cambios_solo_se_aplicaran')}</Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={loading}
        >
          {t('common.cancel')}</Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          disabled={loading || !formik.isValid || vehiclesLoading}
          sx={{ minWidth: 120 }}
        >
          {loading ? t('sensorForm.saving') : isEditing ? t('audit.actions.UPDATE') : t('hardcoded.components_alarms_alarmform.crear_alarmas')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlarmForm;